import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ArrowRight, ChevronRight, FolderOpen, ShieldAlert, ShieldCheck } from 'lucide-react'
import { useStudioStore } from '@/store/studio-store'
import { DashboardContainer } from '../components/dashboard-container'
import { DataLoadError } from '@/components/data-load-error'
import { getProject } from '@/services/project-api'
import { pushStudioRoute } from '@/lib/studio-navigation'
import { getGenerationOrphanCopy } from '../generation-orphan-copy'
import { HeroChatInput } from '../components/hero-chat-input'
import { takeIntakeCarry } from '@/features/chat/services/intake-carry'
import { useStudioLocale } from '@/i18n'
import { getCoogoStudioHomeRenewalCopy } from '../coogo-studio-home-renewal-copy'
import { getAuthorityContinuityCopy } from '../authority-continuity-copy'
import {
  buildShowroomStudioPrompt,
  readShowroomStudioHandoff,
  removeShowroomStudioHandoff,
} from '@/lib/landing-studio-handoff'
import {
  clearStudioFeaturedOutcomeDraftIfCurrent,
  createStudioFeaturedOutcomeDraft,
  persistStudioFeaturedOutcomeDraft,
  persistStudioFeaturedOutcomeDraftIfCurrent,
  readStudioFeaturedOutcomeDraft,
} from "@/features/productization/studio-featured-outcome-draft";
import { STUDIO_NEWSLETTER_MEMBERSHIP_SKU_ID } from "@/features/productization/studio-outcome-catalog";
import {
  readStudioGenerationAuthority,
  type StudioGenerationAuthorityReadback,
} from '@/features/generation-authority/generation-authority-readback'
import { buildAuthorityContinuityViewModel } from '../lib/authority-continuity-view-model'
import { STARTER_APP_DOOR } from '../lib/starter-app-door'
import { handleProjectCreateError } from '@/store/project-create-error'
import { toast } from '@/store/toast-store'
import {
  buildHomeContinuityModel,
  type HomeContinuityProject,
} from '../lib/home-continuity-model'
import { AuthorityContinuityRegion } from '../components/authority-continuity-region'
import { activateStudioAuthorityTask } from '@/shell/ide-v2/studio-authority-activation'
import type { StudioRoute } from '@/store/types'
import { HomeWorkContext } from '../components/home-work-context'
import { openStudioLaunchChat } from '../lib/studio-launch-chat'

type AuthorityContinuityLoadState =
  | { status: 'idle' }
  | { status: 'loading'; projectId: string }
  | {
      status: 'settled'
      projectId: string
      readback: StudioGenerationAuthorityReadback
    }
  | { status: 'error'; projectId: string }

function isCanonicalStudioHomeLocation(): boolean {
  if (typeof window === 'undefined') return false
  return window.location.pathname === '/studio' || window.location.pathname === '/studio/'
}

function formatTimeAgo(dateStr: string, locale: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const mins = Math.floor(diffMs / 60_000);
  const relativeTime = new Intl.RelativeTimeFormat(locale, {
    numeric: "auto",
  });
  if (mins < 1) return relativeTime.format(0, "minute");
  if (mins < 60) return relativeTime.format(-mins, "minute");
  const hours = Math.floor(mins / 60);
  if (hours < 24) return relativeTime.format(-hours, "hour");
  const days = Math.floor(hours / 24);
  if (days < 30) return relativeTime.format(-days, "day");
  return new Date(dateStr).toLocaleDateString(locale);
}

function normalizeHomeProjectName(name: string): string {
  return name.trim().replace(/\s+/g, ' ').toLocaleLowerCase()
}

function uniqueAttentionProjects(
  attention: readonly HomeContinuityProject[],
  recent: readonly HomeContinuityProject[],
): HomeContinuityProject[] {
  const seenNames = new Set<string>()
  const seenIds = new Set(recent.map((item) => item.project.id))
  for (const item of recent) {
    seenNames.add(normalizeHomeProjectName(item.project.name))
  }

  return attention.filter((item) => {
    if (seenIds.has(item.project.id)) return false
    const displayName = normalizeHomeProjectName(item.project.name)
    if (seenNames.has(displayName)) return false
    seenNames.add(displayName)
    return true
  })
}

export function OverviewPage() {
  const { locale, strings } = useStudioLocale()
  const t = strings.home
  const renewalCopy = getCoogoStudioHomeRenewalCopy(locale)
  const authorityCopy = getAuthorityContinuityCopy(locale)
  const setStudioRoute = useStudioStore((state) => state.setStudioRoute)
  const openServerProject = useStudioStore((state) => state.openServerProject)
  const createServerProject = useStudioStore((state) => state.createServerProject)
  const principalId = useStudioStore((state) => state.user?.id ?? null)
  const chatSidebarOpen = useStudioStore((state) => state.chatSidebarOpen)
  const canonicalHomeLocation = isCanonicalStudioHomeLocation()
  // Whether the composer is holding anything the creator still needs. It is
  // the only thing that knows, so it reports; this page never enumerates
  // which of its states count. See `composer-rest-state.ts`.
  const [composerAtRest, setComposerAtRest] = useState(true)
  // W21 rung ② — a document carried over from the chat thread composer. The
  // channel is one-shot (take = read-and-clear), so the take must NOT run in
  // a state initializer: this screen mounts for one transient render on
  // every /studio/* deep link (see the projects comment below), and a take
  // during that render would consume the carry into a hero that immediately
  // unmounts. Gate on the composer actually being shown.
  const [intakeCarryPrompt, setIntakeCarryPrompt] = useState<string | null>(null)

  /**
   * Home is always a place to start, even when a project remains selected in
   * the workspace store. Only an open chat dock may temporarily take over the
   * resting composer. The canonical-location gate prevents the transient home
   * render used while a deep link synchronizes from consuming carried input.
   */
  const showHomeComposer =
    canonicalHomeLocation && (!composerAtRest || !chatSidebarOpen)

  useEffect(() => {
    if (!showHomeComposer) return
    const carried = takeIntakeCarry(principalId)
    if (carried) setIntakeCarryPrompt(carried)
  }, [showHomeComposer, principalId])

  // Saved work comes from the store, NOT from a private `listProjects()` call
  // here. This screen mounts on every `/studio/*` deep link for one render
  // (the shell's route starts at 'home' until the URL sync runs), so its own
  // read used to fire on screens it never stayed on — two more concurrent
  // `/api/projects` requests per navigation, invisible to the rest of the app.
  // Through the store it shares the single de-duplicated read.
  const projects = useStudioStore((state) => state.userProjects)
  const isLoadingUserProjects = useStudioStore((state) => state.isLoadingUserProjects)
  const userProjectsLoadedAt = useStudioStore((state) => state.userProjectsLoadedAt)
  const userProjectsError = useStudioStore((state) => state.userProjectsError)
  const loadUserProjects = useStudioStore((state) => state.loadUserProjects)
  const loading = isLoadingUserProjects || (userProjectsLoadedAt === null && !userProjectsError)
  const [outcomeDraft, setOutcomeDraft] = useState(() => readStudioFeaturedOutcomeDraft(principalId))
  const [authorityLoad, setAuthorityLoad] = useState<AuthorityContinuityLoadState>({ status: 'idle' })
  const [authorityReadAttempt, setAuthorityReadAttempt] = useState(0)
  const authorityReadRef = useRef<{
    key: string
    request: ReturnType<typeof getProject>
  } | null>(null)
  const principalScopedOutcomeDraft = outcomeDraft?.principalId === principalId ? outcomeDraft : null
  const outcomeIntent = principalScopedOutcomeDraft?.outcomeIntent ?? null

  useEffect(() => {
    void loadUserProjects();
  }, [loadUserProjects]);

  useEffect(() => {
    const persistedDraft = readStudioFeaturedOutcomeDraft(principalId)
    if (!principalId) {
      setOutcomeDraft(persistedDraft)
      return
    }

    const showroomHandoff = readShowroomStudioHandoff()
    if (!showroomHandoff) {
      setOutcomeDraft(persistedDraft)
      return
    }

    const draft = createStudioFeaturedOutcomeDraft({
      principalId,
      skuId: showroomHandoff.skuId,
      prompt: buildShowroomStudioPrompt(showroomHandoff, locale),
    })
    if (!persistStudioFeaturedOutcomeDraft(draft)) {
      setOutcomeDraft(persistedDraft)
      return
    }

    removeShowroomStudioHandoff(showroomHandoff)
    setOutcomeDraft(draft)
  }, [locale, principalId])

  // `locale` is a real dependency, not decoration: the proof-health labels
  // inside the model are localized, so a language switch must rebuild it.
  const homeContinuity = useMemo(
    () => buildHomeContinuityModel({ locale, strings }, projects, Date.now()),
    [locale, strings, projects],
  )
  const latestContinuity = homeContinuity.recent[0] ?? null
  const latestProject = latestContinuity?.project ?? null
  const startAnotherWayRoutes = [
    ['new-project', renewalCopy.newProject],
    ['import', renewalCopy.sources],
  ] as const
  const attentionProjects = uniqueAttentionProjects(
    homeContinuity.attention,
    homeContinuity.recent,
  )
  const latestProofHealth = latestContinuity?.proofHealth ?? null
  // G1 — an orphaned generation (pending marker older than 10 minutes) must
  // not be greeted as "ready to continue"; label it honestly instead.
  const latestGenerationOrphaned = latestContinuity?.generationOrphaned ?? false
  const orphanCopy = getGenerationOrphanCopy(locale)

  useEffect(() => {
    if (
      !latestProject ||
      !showHomeComposer ||
      !canonicalHomeLocation
    ) {
      setAuthorityLoad({ status: 'idle' })
      return
    }

    const projectId = latestProject.id
    const requestKey = `${projectId}:${authorityReadAttempt}`
    if (authorityReadRef.current?.key !== requestKey) {
      authorityReadRef.current = {
        key: requestKey,
        request: getProject(projectId),
      }
    }
    const request = authorityReadRef.current.request
    let current = true
    setAuthorityLoad({ status: 'loading', projectId })

    void request.then((project) => {
      if (!current) return
      if (!project || project.id !== projectId) {
        setAuthorityLoad({ status: 'error', projectId })
        return
      }
      setAuthorityLoad({
        status: 'settled',
        projectId,
        readback: readStudioGenerationAuthority(project),
      })
    })

    return () => {
      current = false
    }
  }, [
    authorityReadAttempt,
    canonicalHomeLocation,
    latestProject,
    showHomeComposer,
  ])

  const authorityReadback = useMemo<StudioGenerationAuthorityReadback | null>(() => {
    if (
      !canonicalHomeLocation ||
      !showHomeComposer ||
      loading ||
      (userProjectsError && projects.length === 0)
    ) {
      return null
    }
    if (!latestProject) {
      return { status: 'absent', projectId: 'studio-new-project' }
    }
    if (
      authorityLoad.status !== 'settled' ||
      authorityLoad.projectId !== latestProject.id
    ) {
      return null
    }
    return authorityLoad.readback
  }, [
    authorityLoad,
    canonicalHomeLocation,
    latestProject,
    loading,
    projects.length,
    showHomeComposer,
    userProjectsError,
  ])

  const authorityContinuityModel = useMemo(() => {
    if (!authorityReadback) return null
    const blockingCount =
      authorityReadback.status === 'available'
        ? Math.max(
            authorityReadback.envelope.sourceSufficiency.blockerDimensionIds.length,
            authorityReadback.envelope.sourceSufficiency.questions.length,
          )
        : 0
    return buildAuthorityContinuityViewModel({
      readback: authorityReadback,
      projectName: latestProject?.name,
      blockingCount,
      locale,
      hasExistingWork: projects.length > 0,
    })
  }, [authorityReadback, latestProject?.name, locale, projects.length])

  const handleOpen = useCallback((projectId: string) => openServerProject(projectId), [openServerProject])

  const handleGoToRoute = useCallback((route: StudioRoute) => {
    setStudioRoute(route)
    pushStudioRoute(route)
  }, [setStudioRoute])

  const handleGoToProjects = useCallback(() => {
    handleGoToRoute('projects')
  }, [handleGoToRoute]);

  const handleGoToImport = useCallback(() => {
    handleGoToRoute('import')
  }, [handleGoToRoute]);

  /**
   * The empty-workspace door to a running app. Reuses the EXISTING Starters
   * path verbatim — `createServerProject(name, templateId)` →
   * `loadTemplateWithBundle('/apps/<id>/…')` → `openServerProject`, which is
   * what puts `view: 'workspace'` / `viewMode: 'preview'` on screen. Nothing
   * new is invented here and the parked template catalogue is not consulted:
   * see `lib/starter-app-door.ts` for why the catalogue cannot be the door.
   *
   * The rejection is handled through the SAME shared handler the Starters
   * gallery uses (`handleProjectCreateError`), so a mid-session auth expiry
   * routes to sign-in instead of dying as an unhandled rejection, and a
   * producer-marked failure is not double-toasted.
   */
  const [starterDoorPending, setStarterDoorPending] = useState(false)
  const handleOpenStarterApp = useCallback(() => {
    setStarterDoorPending(true)
    void createServerProject(
      renewalCopy.starterDoorProjectName,
      STARTER_APP_DOOR.templateId,
    )
      .catch((err: unknown) =>
        handleProjectCreateError(
          err,
          {
            toastError: (message) => toast.error(message),
            signOut: () => void useStudioStore.getState().signOut(),
          },
          renewalCopy.starterDoorFailed,
        ),
      )
      .finally(() => setStarterDoorPending(false))
  }, [
    createServerProject,
    renewalCopy.starterDoorFailed,
    renewalCopy.starterDoorProjectName,
  ]);

  const handleContinueChat = useCallback(() => {
    document
      .querySelector<HTMLTextAreaElement>('[data-testid="hero-chat-input"]')
      ?.focus()
  }, [])

  const handleOpenProjectChat = useCallback(() => {
    if (!latestProject) return
    void Promise.resolve(handleOpen(latestProject.id)).then(() => {
      const state = useStudioStore.getState()
      if (
        state.currentProjectId !== latestProject.id ||
        state.projectLoadError
      ) return
      openStudioLaunchChat({
        source: 'project-open',
        projectName: latestProject.name,
      })
    })
  }, [handleOpen, latestProject])

  const handleReviewAuthority = useCallback(() => {
    if (!latestProject) return
    const projectId = latestProject.id
    void Promise.resolve(handleOpen(projectId)).then(() => {
      if (useStudioStore.getState().currentProjectId === projectId) {
        activateStudioAuthorityTask({
          domain: 'primary',
          outputKind: 'app',
          railItem: 'evidence',
        })
      }
    })
  }, [handleOpen, latestProject])

  const handleRetryAuthorityRead = useCallback(() => {
    authorityReadRef.current = null
    setAuthorityReadAttempt((attempt) => attempt + 1)
  }, [])

  const handleFeaturedOutcome = useCallback(() => {
    const draft = createStudioFeaturedOutcomeDraft({
      principalId,
      skuId: STUDIO_NEWSLETTER_MEMBERSHIP_SKU_ID,
      prompt: t.featuredOutcome.prompt,
    })
    persistStudioFeaturedOutcomeDraft(draft)
    setOutcomeDraft(draft)
  }, [principalId, t.featuredOutcome.prompt])

  const handleOutcomeIntentConsumed = useCallback((draftId: string) => {
    clearStudioFeaturedOutcomeDraftIfCurrent(draftId)
    setOutcomeDraft((current) => (current?.draftId === draftId ? null : current))
  }, [])

  const handleOutcomeDraftChange = useCallback(
    (draftId: string, prompt: string, awaitingClarification: boolean) => {
      const currentDraft = principalScopedOutcomeDraft;
      if (!currentDraft || currentDraft.draftId !== draftId) return;
      persistStudioFeaturedOutcomeDraftIfCurrent(
        draftId,
        createStudioFeaturedOutcomeDraft({
          draftId,
          principalId,
          skuId: currentDraft.outcomeIntent.skuId,
          prompt,
          awaitingClarification,
        }),
      );
    },
    [principalId, principalScopedOutcomeDraft],
  );

  const needsReview =
    latestProofHealth?.status === 'review' ||
    latestProofHealth?.status === 'blocked' ||
    authorityContinuityModel?.tone === 'blocked'

  // Measured live defect (2026-08-06 appliance review): the hardcoded
  // slate-50 pane ignored the dark theme, leaving the whole home pane light
  // while the sidebar, topbar, and chat dock went dark. The light-mode pixel
  // stays identical; dark mode falls back to the semantic background. Full
  // hardcoded-color cleanup rides the color ratchet.
  return (
    <div className="h-full overflow-y-auto bg-background">
      <DashboardContainer className="max-w-[880px] px-4 py-10 sm:px-6 lg:py-[52px]">
        <section data-testid="overview-making-stage" className="mx-auto max-w-[720px] pb-8">
          <div className="min-w-0">
            {/* FINDING-003: the headline that explains this page renders BEFORE
                the composer it labels — previously the input card came first and
                the H1 appeared mid-scroll, inverting the reading order. */}
            {loading ? (
              <div className="mt-2 text-center">
                <h1 data-route-heading="home" className="text-balance text-[26px] font-bold tracking-[-0.025em] text-foreground sm:text-[30px]">
                  {renewalCopy.firstTitle}
                </h1>
                <div className="mx-auto mt-4 h-5 w-full max-w-[420px] animate-pulse rounded-lg bg-card/80" />
              </div>
            ) : userProjectsError && projects.length === 0 ? (
              <h1 data-route-heading="home" className="mt-2 text-balance text-center text-[26px] font-bold tracking-[-0.025em] text-foreground sm:text-[30px]">
                {renewalCopy.firstTitle}
              </h1>
            ) : latestProject && latestProofHealth ? (
              <div className="mt-2 space-y-4" data-testid="overview-returning-user">
                <h1 data-route-heading="home" className="text-balance text-[26px] font-bold tracking-[-0.025em] text-foreground sm:text-[30px]">
                  {renewalCopy.firstTitle}
                </h1>
              </div>
            ) : (
              <div data-testid="overview-first-user" className="mt-2 pb-1 text-center">
                <h1 data-route-heading="home" className="text-balance text-[26px] font-bold tracking-[-0.025em] text-foreground sm:text-[30px]">
                  {renewalCopy.firstTitle}
                </h1>
                <p className="mx-auto mt-2 max-w-[620px] text-sm leading-6 text-[#64748B]">
                  {renewalCopy.firstSubtitle}
                </p>
              </div>
            )}

            <div
              data-testid="overview-chat-start"
              aria-label={t.startWithChat}
              hidden={!showHomeComposer}
              className="mt-6"
            >
              <HeroChatInput
                key={`home-composer:${principalId ?? "anonymous"}`}
                showGreeting={false}
                homeState={latestProject ? 'returning' : 'first'}
                onSourceSelect={handleGoToImport}
                className="w-full px-0"
                prefillRequest={principalScopedOutcomeDraft?.prompt ?? null}
                intakeCarryPrompt={intakeCarryPrompt}
                outcomeIntent={outcomeIntent}
                outcomeDraftId={principalScopedOutcomeDraft?.draftId ?? null}
                outcomeAwaitingClarification={
                  principalScopedOutcomeDraft?.awaitingClarification ?? false
                }
                onOutcomeIntentConsumed={handleOutcomeIntentConsumed}
                onOutcomeDraftChange={handleOutcomeDraftChange}
                onRestStateChange={setComposerAtRest}
                ariaDescribedBy="overview-composer-description"
              />
            </div>

            <p
              id="overview-composer-description"
              hidden={!showHomeComposer}
              className="mx-auto mt-3 max-w-[660px] text-center text-[13px] leading-5 text-[#64748B]"
            >
              {renewalCopy.conversationHint}
            </p>

            {!loading && !userProjectsError ? (
              <HomeWorkContext
                copy={renewalCopy}
                projectName={latestProject?.name ?? null}
                onOpenBot={() => handleGoToRoute('bots')}
                onOpenProjectChat={handleOpenProjectChat}
              />
            ) : null}

            {!loading && userProjectsError && projects.length === 0 ? (
              // A failed read is not "you have nothing yet". Say the read
              // failed and offer the retry instead of rendering the
              // first-time screen over a server error.
              <div className="mt-5">
                <DataLoadError
                  label={authorityCopy.savedWork}
                  message={authorityCopy.dataLoadMessage(authorityCopy.savedWork)}
                  retryLabel={authorityCopy.retry}
                  onRetry={() => { void loadUserProjects({ force: true }) }}
                  testId="overview-saved-work-error"
                />
              </div>
            ) : null}

            {!loading && latestProject && latestProofHealth ? (
              <section
                aria-labelledby="overview-recent-work-title"
                data-testid="overview-recent-work"
                className="mt-6"
              >
                <div className="flex items-center justify-between gap-3">
                  <h2
                    id="overview-recent-work-title"
                    className="text-sm font-semibold text-foreground"
                  >
                    {renewalCopy.recentWork}
                  </h2>
                  <button
                    type="button"
                    onClick={handleGoToProjects}
                    className="inline-flex min-h-9 items-center gap-1 rounded-md px-2 text-xs font-semibold text-primary transition-colors hover:bg-primary/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/25"
                  >
                    {renewalCopy.viewAllProjects}
                    <ChevronRight className="h-3.5 w-3.5" aria-hidden />
                  </button>
                </div>
                <article
                  data-testid="overview-readiness"
                  className="mt-3 flex flex-col gap-4 rounded-[14px] border border-border bg-card px-5 py-4 shadow-[0_1px_2px_rgb(15_23_42_/_0.02)] sm:flex-row sm:items-center"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[9px] bg-[#102033] text-sm font-bold text-white">
                    {latestProject.name.trim().charAt(0).toUpperCase() || 'C'}
                  </span>
                  <div className="min-w-0 flex-1">
                    <h3 className="truncate text-base font-bold text-foreground">{latestProject.name}</h3>
                    <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 font-mono text-xs text-[#64748B]">
                      <span
                        data-testid={latestGenerationOrphaned ? 'overview-generation-orphan' : undefined}
                        className={latestGenerationOrphaned ? 'font-semibold text-[#B45309]' : undefined}
                      >
                        {latestGenerationOrphaned
                          ? orphanCopy.label
                          : renewalCopy.projectStates[latestProject.status]}
                      </span>
                      <span aria-hidden>·</span>
                      <span>{latestProofHealth.label}</span>
                      <span aria-hidden>·</span>
                      <span>{formatTimeAgo(latestProject.updated_at, locale)}</span>
                    </p>
                  </div>
                  <div className="flex shrink-0 flex-wrap items-center gap-2">
                    {needsReview ? (
                      <button
                        type="button"
                        onClick={handleReviewAuthority}
                        className="inline-flex h-10 items-center gap-1.5 rounded-lg bg-primary px-4 text-sm font-semibold text-white transition-colors hover:bg-[#0284C7] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
                      >
                        <ShieldAlert className="h-3.5 w-3.5" aria-hidden />
                        {renewalCopy.reviewProject}
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleOpen(latestProject.id)}
                        className="inline-flex h-10 items-center rounded-lg bg-primary px-4 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
                      >
                        {latestGenerationOrphaned
                          ? orphanCopy.resume
                          : renewalCopy.openProject}
                      </button>
                    )}
                  </div>
                  <span className="sr-only">{t.readiness}</span>
                </article>
                {homeContinuity.recent.length > 1 ? (
                  <div className="mt-2 divide-y divide-border overflow-hidden rounded-xl border border-border bg-card">
                    {homeContinuity.recent.slice(1).map((item) => (
                      <HomeContinuityProjectRow
                        key={item.project.id}
                        item={item}
                        onOpen={handleOpen}
                        orphanedGenerationLabel={orphanCopy.label}
                        testId="overview-recent-work-row"
                      />
                    ))}
                  </div>
                ) : null}
              </section>
            ) : null}

            {authorityContinuityModel ? (
              <div className="mt-5 min-w-0">
                <AuthorityContinuityRegion
                  model={authorityContinuityModel}
                  onContinueChat={handleContinueChat}
                  onReviewContract={handleReviewAuthority}
                  onOpenConformance={handleReviewAuthority}
                />
              </div>
            ) : latestProject &&
              authorityLoad.status === 'error' &&
              authorityLoad.projectId === latestProject.id &&
              canonicalHomeLocation &&
              showHomeComposer ? (
              <div className="mt-5" data-testid="overview-authority-read-error">
                <DataLoadError
                  label={authorityCopy.savedProjectDetails}
                  message={authorityCopy.dataLoadMessage(authorityCopy.savedProjectDetails)}
                  retryLabel={authorityCopy.retry}
                  onRetry={handleRetryAuthorityRead}
                  testId="overview-authority-read-error-detail"
                />
              </div>
            ) : null}

            {!loading && !userProjectsError && !latestProject ? (
              <>
                <section
                  aria-labelledby="overview-empty-recent-work-title"
                  className="mt-6"
                  data-testid="overview-no-work"
                >
                  <h2
                    id="overview-empty-recent-work-title"
                    className="text-sm font-semibold text-foreground"
                  >
                    {renewalCopy.recentWork}
                  </h2>
                  <div className="mt-3 flex items-start gap-3 rounded-xl border border-dashed border-border bg-muted/20 px-4 py-3">
                    <FolderOpen
                      className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground/60"
                      strokeWidth={1.5}
                      aria-hidden
                    />
                    <div>
                      <p className="text-sm font-semibold text-foreground">
                        {renewalCopy.noWorkYet}
                      </p>
                      <p className="mt-0.5 text-xs leading-5 text-muted-foreground">
                        {renewalCopy.projectChatContextUnavailable}
                      </p>
                    </div>
                  </div>
                </section>
                <div
                  data-testid="overview-starter-door"
                  className="mt-3 flex flex-col gap-3 rounded-lg bg-muted/30 px-4 py-3 sm:flex-row sm:items-center"
                >
                  <p className="min-w-0 flex-1 text-xs leading-5 text-muted-foreground">
                    {renewalCopy.starterDoorLead}
                  </p>
                  <button
                    type="button"
                    data-testid="overview-starter-door-action"
                    onClick={handleOpenStarterApp}
                    disabled={starterDoorPending}
                    className="inline-flex min-h-9 shrink-0 items-center justify-center gap-1.5 rounded-md bg-primary px-3 text-xs font-semibold text-primary-foreground transition-colors hover:bg-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/25 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {renewalCopy.starterDoorAction}
                    <ArrowRight className="h-3.5 w-3.5" aria-hidden />
                  </button>
                </div>
              </>
            ) : null}

            {!loading && attentionProjects.length > 0 ? (
              <section
                data-testid="overview-needs-attention"
                aria-labelledby="overview-needs-attention-title"
                className="mt-8"
              >
                <h2
                  id="overview-needs-attention-title"
                  className="text-base font-bold text-foreground"
                >
                  {renewalCopy.needsAttention}
                </h2>
                <div className="mt-3 divide-y divide-border overflow-hidden rounded-xl border border-border bg-card">
                  {attentionProjects.map((item) => (
                    <HomeContinuityProjectRow
                      key={item.project.id}
                      item={item}
                      onOpen={handleOpen}
                      orphanedGenerationLabel={orphanCopy.label}
                      testId="overview-needs-attention-row"
                    />
                  ))}
                </div>
              </section>
            ) : null}

            {!loading ? (
              <details
                data-testid="overview-start-another-way"
                className="group mt-7 rounded-xl border border-border bg-card"
              >
                <summary className="flex min-h-12 cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/25 [&::-webkit-details-marker]:hidden">
                  <span>
                    <span className="block text-sm font-semibold text-foreground">
                      {renewalCopy.startAnotherWay}
                    </span>
                    <span className="mt-0.5 block text-xs leading-5 text-muted-foreground">
                      {renewalCopy.advancedStartHint}
                    </span>
                  </span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-90" aria-hidden />
                </summary>
                <div className="border-t border-border px-4 pb-4 pt-3">
                  <div className="grid gap-2 sm:grid-cols-2">
                    {startAnotherWayRoutes.map(([route, label]) => (
                      <button
                        key={route}
                        type="button"
                        onClick={() => handleGoToRoute(route)}
                        className="inline-flex min-h-11 items-center justify-between rounded-lg border border-border bg-background px-3 text-left text-sm font-semibold text-foreground transition-colors hover:border-primary/35 hover:bg-primary/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/25"
                      >
                        {label}
                        <ArrowRight className="h-4 w-4 text-muted-foreground" aria-hidden />
                      </button>
                    ))}
                  </div>

                </div>
              </details>
            ) : null}

            <details
              data-testid="overview-showroom"
              open={Boolean(outcomeIntent)}
              className="group mx-auto mt-5 max-w-[680px]"
            >
              <summary className="mx-auto flex min-h-10 w-fit cursor-pointer list-none items-center gap-1 rounded-lg px-3 text-sm font-semibold text-[#0369A1] transition-colors hover:bg-[#F0F9FF] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/25 [&::-webkit-details-marker]:hidden">
                {renewalCopy.showWorkingProducts}
                <ChevronRight className="h-3.5 w-3.5 transition-transform group-open:rotate-90" />
              </summary>
              <p className="mb-3 mt-1 text-center text-[13px] leading-5 text-[#64748B]">{renewalCopy.showroomHint}</p>
              <section data-testid="overview-featured-outcome" aria-labelledby="overview-featured-outcome-title">
                <button
                  type="button"
                  data-testid="overview-featured-outcome-action"
                  aria-pressed={Boolean(outcomeIntent)}
                  onClick={handleFeaturedOutcome}
                  className="group/card flex w-full items-start gap-4 rounded-xl border border-border bg-card p-4 text-left shadow-[0_2px_8px_rgb(15_23_42_/_0.04)] transition-colors hover:border-[#BAE6FD] hover:bg-[#FCFEFF] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/25 aria-pressed:border-primary aria-pressed:bg-[#F0F9FF]/60 sm:p-5"
                >
                  <span className="mt-0.5 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-border bg-[#FFFDF8] text-[#7C3AED]">
                    <ShieldCheck className="h-5 w-5" strokeWidth={1.8} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-[11px] font-bold uppercase tracking-[0.08em] text-[#7C3AED]">
                      {t.featuredOutcome.eyebrow}
                    </span>
                    <span
                      id="overview-featured-outcome-title"
                      className="mt-1 block text-base font-bold text-foreground"
                    >
                      {t.featuredOutcome.title}
                    </span>
                    <span className="mt-1 block text-sm leading-6 text-[#64748B]">{t.featuredOutcome.description}</span>
                    <span className="mt-2 block text-xs leading-5 text-[#64748B]">{t.featuredOutcome.boundary}</span>
                  </span>
                  <span className="mt-1 hidden shrink-0 items-center gap-1 text-xs font-semibold text-[#0369A1] sm:inline-flex">
                    {t.featuredOutcome.action}
                    <ArrowRight
                      className="h-3.5 w-3.5 transition-transform group-hover/card:translate-x-0.5"
                      aria-hidden
                    />
                  </span>
                </button>
              </section>
            </details>

          </div>
        </section>
      </DashboardContainer>
    </div>
  );
}

function HomeContinuityProjectRow({
  item,
  onOpen,
  orphanedGenerationLabel,
  testId,
}: {
  item: HomeContinuityProject
  onOpen: (projectId: string) => void
  orphanedGenerationLabel: string
  testId: string
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={() => onOpen(item.project.id)}
      className="flex min-h-12 w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-background focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/25"
    >
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-semibold text-foreground">
          {item.project.name}
        </span>
        <span className="mt-0.5 block text-xs text-[#64748B]">
          {item.proofHealth.label}
          {item.generationOrphaned ? ` · ${orphanedGenerationLabel}` : ''}
        </span>
      </span>
      <ChevronRight className="h-4 w-4 shrink-0 text-[#64748B]" aria-hidden />
    </button>
  )
}
